import accounting from "accounting"

import { ContentTagsCell, ContentTagsCellSkeleton, CreatorCell, CreatorCellSkeleton, NotesPreviewCell, NotesPreviewCellSkeleton, PitchStatusBadge, PitchStatusBadgeSkeleton, PlatformCell, PlatformCellSkeleton, RowActionsSkeleton, type TableColumn } from "@/components/tableKit"
import { Skeleton } from "@/components/ui/skeleton"
import { getCurrencySymbol } from "@/shared/currency"
import type { Booking, Campaign, Currency, Platform, User } from "@/shared/types"
import type { PitchRow } from "../campaign"
import { PitchRowMenu } from "./PitchRowMenu"

export type PitchColumnsOptions = {
    platforms: Platform[]
    showNotes: boolean
    showRates: boolean
    currency?: Currency
    user: User | undefined
    campaign: Campaign
    campaignId?: string
    bookings: Booking[]
    booker?: User
    onApproved: () => void
}

/** Pitches tab columns. Notes and rates follow showNotesAndRates. */
export function pitchColumns({
    platforms,
    showNotes,
    showRates,
    currency = "USD",
    user,
    campaign,
    campaignId,
    bookings,
    booker,
    onApproved,
}: PitchColumnsOptions): TableColumn<PitchRow>[] {
    const columns: TableColumn<PitchRow>[] = [
        {
            id: "creator",
            header: "Creator",
            cell: (row) => <CreatorCell talent={row.talent} />,
            skeleton: <CreatorCellSkeleton />,
        },
    ]

    if (showRates) {
        columns.push({
            id: "price",
            header: "Price",
            cell: (row) => (
                <span>
                    {accounting.formatMoney(row.price, getCurrencySymbol(currency), 0, ",", ".")}
                </span>
            ),
            className: "min-w-24",
            skeleton: <Skeleton className="h-4 w-16" />,
        })
    }

    for (const platform of platforms) {
        columns.push({
            id: platform,
            header: platform,
            cell: (row) => <PlatformCell platform={platform} talent={row.talent} />,
            skeleton: <PlatformCellSkeleton />,
        })
    }

    columns.push({
        id: "content",
        header: "Content",
        cell: (row) => <ContentTagsCell tags={row.talent?.contentTags} />,
        className: "min-w-64",
        skeleton: <ContentTagsCellSkeleton />,
    })

    columns.push({
        id: "status",
        header: "Status",
        cell: (row) => <PitchStatusBadge status={row.status} />,
        skeleton: <PitchStatusBadgeSkeleton />,
    })

    if (showNotes) {
        columns.push({
            id: "notes",
            header: "Notes",
            cell: (row) => <NotesPreviewCell notes={row.notes} />,
            className: "min-w-56 max-w-96 whitespace-normal",
            skeleton: <NotesPreviewCellSkeleton />,
        })
    }

    columns.push({
        id: "actions",
        header: "Actions",
        sticky: true,
        skeleton: <RowActionsSkeleton />,
        cell: (row) => (
            <PitchRowMenu
                row={row}
                user={user}
                campaign={campaign}
                campaignId={campaignId}
                bookings={bookings}
                booker={booker}
                onApproved={onApproved}
            />
        ),
    })

    return columns
}
